import { apiFetch } from '../api.js';
import { appHref } from '../globals.js';
import { getStoredSession } from '../auth-storage.js';

/**
 * A small plain screen, not part of `administration`'s manifest-driven views
 * — a password is platform-side, never a collaborative field (ADR-0018).
 * Unlike login, a specific failure message here is fine: the caller is
 * already authenticated, so there's no brute-force signal to protect.
 */
export function mountChangePassword(root: HTMLElement): void {
  const session = getStoredSession();
  if (!session) {
    window.location.assign(appHref('/login'));
    return;
  }

  root.innerHTML = `
    <div class="container" style="max-width:420px;margin-top:8vh">
      <div class="cf-card p-4">
        <h1 class="h4 mb-3">Change password</h1>
        <form id="cpForm" novalidate>
          <div class="mb-3">
            <label class="form-label" for="cpCurrent">Current password</label>
            <input class="form-control" id="cpCurrent" type="password" autocomplete="current-password" required>
          </div>
          <div class="mb-3">
            <label class="form-label" for="cpNew">New password</label>
            <input class="form-control" id="cpNew" type="password" autocomplete="new-password" minlength="8" required>
            <div class="form-text">At least 8 characters.</div>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-primary" type="submit" id="cpSubmit">Change password</button>
            <a class="btn btn-outline-secondary" href="${appHref('/')}">Back</a>
          </div>
          <div class="mt-2" style="font-size:.85rem" id="cpStatus" aria-live="polite"></div>
        </form>
      </div>
    </div>`;

  const form = root.querySelector<HTMLFormElement>('#cpForm')!;
  const status = root.querySelector<HTMLElement>('#cpStatus')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#cpSubmit')!;
  const currentInput = root.querySelector<HTMLInputElement>('#cpCurrent')!;
  const newInput = root.querySelector<HTMLInputElement>('#cpNew')!;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submit();
  });

  async function submit(): Promise<void> {
    submitBtn.disabled = true;
    status.textContent = '';
    try {
      const res = await apiFetch('/auth/change-password', {
        method: 'POST',
        token: session!.token,
        body: { currentPassword: currentInput.value, newPassword: newInput.value },
      });
      if (res.ok) {
        status.className = 'mt-2 text-success';
        status.textContent = 'Password changed.';
        form.reset();
      } else {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        status.className = 'mt-2 text-danger';
        status.textContent = data.error === 'password_too_short'
          ? 'New password must be at least 8 characters.'
          : 'Current password is incorrect.';
      }
    } catch {
      status.className = 'mt-2 text-danger';
      status.textContent = 'Something went wrong. Try again.';
    } finally {
      submitBtn.disabled = false;
    }
  }
}
